import {
  Label,
  useCreateMutation,
  useWorkspaceLabels,
} from '@gripp/shared-logic';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, Text, TouchableOpacity, View } from 'react-native';
import { SearchInput } from '../../crop-circle/Components/SearchInput';
import { themedStyleSheet } from '../../../themes/themedStyleSheet';

type Props = {
  workspaceId: string;
  initialItems: Label[];
  onChange: (items: Label[]) => void;
};

const CREATE_LABEL = `
  mutation CreateLabel($input: CreateLabelInput!) {
    createLabel(input: $input) {
      id
      name
    }
  }
`;

export const RoutineLabelPickerPanel = ({
  workspaceId,
  initialItems,
  onChange,
}: Props) => {
  const { t } = useTranslation();
  const { labels: availableLabels } = useWorkspaceLabels(workspaceId);
  const [selected, setSelected] = useState<Label[]>(initialItems);
  const [search, setSearch] = useState('');

  useEffect(() => {
    setSelected(initialItems);
  }, [initialItems]);

  const { mutateAsync: createLabel } = useCreateMutation({
    modelName: 'label',
    query: CREATE_LABEL,
  });

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (availableLabels ?? []).filter(
      (l: Label) => !term || l.name.toLowerCase().includes(term)
    );
  }, [availableLabels, search]);

  const isSelected = (label: Label) => selected.some((l) => l.id === label.id);

  const toggle = (label: Label) => {
    const next = isSelected(label)
      ? selected.filter((l) => l.id !== label.id)
      : [...selected, label];
    setSelected(next);
    onChange(next);
  };

  const canCreate =
    !!search.trim() &&
    !(availableLabels ?? []).some(
      (l: Label) => l.name.toLowerCase() === search.trim().toLowerCase()
    );

  const handleCreate = async () => {
    try {
      const created = (await createLabel({
        input: { name: search.trim(), workspace: { id: workspaceId } },
      })) as Label;
      const next = [...selected, created];
      setSelected(next);
      onChange(next);
      setSearch('');
    } catch (error) {
      console.error('Failed to create label', error);
    }
  };

  return (
    <View style={styles.container}>
      <SearchInput
        value={search}
        onChangeText={setSearch}
        placeholder={t('issue.common.searchLabel')}
        height={44}
      />
      <ScrollView contentContainerStyle={styles.pillsContainer}>
        {filtered.map((label: Label) => {
          const active = isSelected(label);
          return (
            <TouchableOpacity
              key={label.id}
              onPress={() => toggle(label)}
              style={[styles.pill, active && styles.pillActive]}
            >
              <Text
                style={[styles.pillText, active && styles.pillTextActive]}
                numberOfLines={1}
              >
                {label.name}
              </Text>
            </TouchableOpacity>
          );
        })}
        {canCreate && (
          <TouchableOpacity
            onPress={handleCreate}
            style={[styles.pill, styles.createPill]}
          >
            <Text style={styles.createPillText}>
              + {t('issue.common.createLabel')} "{search.trim()}"
            </Text>
          </TouchableOpacity>
        )}
      </ScrollView>
    </View>
  );
};

const styles = themedStyleSheet((role) => ({
  container: {
    flex: 1,
    backgroundColor: role.canvas,
    padding: 16,
    gap: 16,
  },
  pillsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    paddingBottom: 16,
  },
  pill: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 20,
    backgroundColor: role.surfaceHover,
    maxWidth: '100%',
  },
  pillActive: {
    backgroundColor: role.surfaceInverse,
  },
  pillText: {
    fontSize: 14,
    color: role.textStrong,
  },
  pillTextActive: {
    color: role.textOnInverse,
  },
  createPill: {
    borderStyle: 'dashed',
    borderWidth: 1,
    borderColor: role.surfaceInverse,
    backgroundColor: 'transparent',
  },
  createPillText: {
    fontSize: 14,
    color: role.textStrong,
    fontWeight: '500',
  },
}));
